// React imports
import React, { useState, useEffect, useRef } from 'react';

// MUI imports
import {
  Modal, Box, Grid, TextField, FormControl, InputLabel, Select, MenuItem, Autocomplete, CircularProgress,
  Tooltip, FormHelperText, IconButton,
} from '@mui/material';

// MUI Icons imports
import CloseIcon from '@mui/icons-material/Close';

// Customized MUI components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import { validatePersonOrEntityName } from "utils/crmValidation";

// Other imports
import { useAuth } from '../../auth/Auth';
import { usePermission } from '../../auth/usePermission';
import colors from "assets/theme/base/colors";
import CustomFieldsSection from 'components/CustomFields/CustomFieldsSection';
import { CountrySelect, StateSelect, CitySelect } from 'components/GeographySelect';
import AddressAutocompleteInput from "../../layouts/Travel/trips/stepForm/fields/AddressAutocompleteInput";

const PAIRING_ERROR = 'Either Email or Phone is required';

const buildInitialFormData = (initialData) => ({
  title: initialData?.title || '',
  first_name: initialData?.first_name || '',
  middle_name: initialData?.middle_name || '',
  last_name: initialData?.last_name || '',
  email: initialData?.email || '',
  phone: initialData?.phone || '',
  mobile_number: initialData?.mobile_number || '',
  mailing_address: initialData?.mailing_address || '',
  mailing_city: initialData?.mailing_city || '',
  mailing_state: initialData?.mailing_state || '',
  mailing_country: initialData?.mailing_country || '',
  mailing_postal_code: initialData?.mailing_postal_code || '',
  department: initialData?.department || '',
  contact_role: initialData?.contact_role || '',
  contact_status: initialData?.contact_status || 'Active',
  notes: initialData?.notes || '',
  primary_contact: initialData?.primary_contact || false,
  account_id: initialData?.account_id || null,
  account_name: initialData?.account_name || '',
  // Carry over custom field values (cf1..cf10) so cloning doesn't drop them.
  ...Object.fromEntries(
    Object.entries(initialData || {}).filter(([key]) => /^cf\d+$/.test(key))
  ),
});


const ContactFormModal = ({
  isOpen,
  onClose,
  onSave,
  managers,
  user,
  title = 'Add New Contact',
  initialData = null,
}) => {

  // Order matters: this determines which error we scroll to first when
  // multiple fields fail validation.
  const fieldOrder = [
    'title',
    'first_name',
    'middle_name',
    'last_name',
    'email',
    'phone',
    'account_id',
    'contact_role',
    'contact_status',
  ];

  const [formData, setFormData] = useState(buildInitialFormData(initialData));

  const { axiosInstance } = useAuth();
  const { isSupportUser } = usePermission('contacts');
  const [selectedManager, setSelectedManager] = useState(null);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [formErrors, setFormErrors] = useState({});
  const [accounts, setAccounts] = useState([]);
  const [isLoadingAccounts, setIsLoadingAccounts] = useState(false);
  const [accountSearchAbortController, setAccountSearchAbortController] = useState(null);
  const [customFields, setCustomFields] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const customFieldsRef = useRef(null);

  const debounce = (func, wait) => {
    let timeout;
    return (...args) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => func.apply(this, args), wait);
    };
  };

  // [ GET ] Retrieve all accounts.

  const fetchAccounts = async (query = '') => {
    try {
      setIsLoadingAccounts(true);
      const response = await axiosInstance.get(`/accounts/?status=active`);
      let filteredAccounts = response.data.map((account) => ({
        id: account.id,
        name: account.name,
      }));

      // If there's a search query, filter the accounts
      if (query) {
        filteredAccounts = filteredAccounts.filter(account =>
          account.name.toLowerCase().includes(query.toLowerCase())
        );
      }

      setAccounts(filteredAccounts);
    } catch (error) {
      console.error("Error fetching accounts:", error);
    } finally {
      setIsLoadingAccounts(false);
    }
  };

  const debouncedFetchAccounts = React.useCallback(
    debounce((searchText) => {
      fetchAccounts(searchText);
    }, 300),
    []
  );

  // Initially load all accounts when the form opens up.

  useEffect(() => {
    if (isOpen) {
      fetchAccounts();
    }
  }, [isOpen]);

  // When the form is open, reset it and seed from initialData (clone) or
  // blank defaults (Add New Contact). Manager prefers the source contact's
  // manager when cloning, falling back to the logged-in user.
  useEffect(() => {
    if (isOpen) {
      setFormData(buildInitialFormData(initialData));
      setSelectedAccount(initialData?.account_id ? { id: initialData.account_id, name: initialData.account_name } : null);
      if (initialData?.manager_id) {
        const matched = (managers || []).find((m) => m.id === initialData.manager_id);
        setSelectedManager(matched || { id: initialData.manager_id, username: '' });
      } else {
        setSelectedManager(user ? { id: user.id, username: user.username } : null);
      }
      setFormErrors({});
      setAccounts([]);
    }

    return () => {
      if (accountSearchAbortController) {
        accountSearchAbortController.abort();
      }
    };
  }, [isOpen]);

  // Add useEffect to fetch custom fields
  useEffect(() => {
    const fetchCustomFields = async () => {
      try {
        const response = await axiosInstance.get('/custom-fields/contacts');
        setCustomFields(response.data || []);
      } catch (error) {
        console.error('Error fetching custom fields:', error);
        setCustomFields([]);
      }
    };

    fetchCustomFields();
  }, []);

  // Handle Input Field changes.

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    const updatedValue = value === "Yes" ? true : value === "No" ? false : value;
    setFormData({
      ...formData,
      [name]: updatedValue,
    });
    setFormErrors((prev) => {
      const next = { ...prev, [name]: '' };
      if ((name === 'email' || name === 'phone') && typeof updatedValue === 'string' && updatedValue.trim()) {
        const other = name === 'email' ? 'phone' : 'email';
        if (next[other] === PAIRING_ERROR) {
          next[other] = '';
        }
      }
      return next;
    });
  };

  // Handle account selection.

  const handleAccountSelect = (event, newValue) => {
    setSelectedAccount(newValue);

    setFormData(prev => ({
      ...prev,
      account_id: newValue?.id || null,
      account_name: newValue?.name || ''
    }));
  };

  // Handle Custom Fields update

  const handleFieldUpdate = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Handle Form Validation errors.

  const handleFieldError = (field, error) => {
    if (error) {
      setFormErrors(prev => ({
        ...prev,
        [field]: error
      }));
    } else {
      setFormErrors(prev => {
        const { [field]: _, ...rest } = prev;
        return rest;
      });
    }
  };

  // Handle Save

  const handleSaveContact = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    try {
      // Validate custom fields
      const isCustomFieldsValid = customFieldsRef.current?.validateAllFields() ?? true;
      const validationErrors = validateForm();
      const isFormValid = Object.keys(validationErrors).length === 0;

      if (isFormValid && isCustomFieldsValid) {
        const formDataToSave = {
          ...formData,
          manager_id: selectedManager?.id,
          created_by: user?.id,
          updated_by: user?.id,
          created_by_name: user?.username,
          updated_by_name: user?.username,
          account_id: selectedAccount?.id,
          account_name: selectedAccount?.name,
        };
        
        await onSave(formDataToSave);
        onClose();
      } else if (!isFormValid) {
         scrollToFirstError(validationErrors);
      }
    } catch (error) {
      console.error('Error saving contact:', error);
      const detail = error?.response?.data?.detail;
      if (typeof detail === 'string' && detail.toLowerCase().includes('email')) {
        setFormErrors((prev) => ({ ...prev, email: detail }));
      }
    } finally {
      setIsSaving(false);
    }
  };

  // Initializing options for Status and Salutation

  const contactStatusOptions = ["Active", "Inactive", "On Hold"];
  const contactTitleOptions = ["Mr.", "Mrs.", "Ms.", "Dr.", "Prof."];

  // Validate form after submission.

  const validateForm = () => {
    const errors = {};

    if (formData.title && !contactTitleOptions.includes(formData.title)) {
      errors.title = 'Please enter valid value for salutation';
    }

    if (!formData.first_name) {
      errors.first_name = 'First name is required';
    } else {
      const firstNameErr = validatePersonOrEntityName(formData.first_name, 'First name');
      if (firstNameErr) errors.first_name = firstNameErr;
    }

    if (!formData.last_name) {
      errors.last_name = 'Last name is required';
    } else {
      const lastNameErr = validatePersonOrEntityName(formData.last_name, 'Last name');
      if (lastNameErr) errors.last_name = lastNameErr;
    }

    if (formData.middle_name) {
      const middleNameErr = validatePersonOrEntityName(formData.middle_name, 'Middle name');
      if (middleNameErr) errors.middle_name = middleNameErr;
    }

    const trimmedEmail = (formData.email || '').trim();
    const trimmedPhone = (formData.phone || '').trim();
    if (!trimmedEmail && !trimmedPhone) {
      errors.email = PAIRING_ERROR;
      errors.phone = PAIRING_ERROR;
    } else {
      // Same regexes as ContactDetail.validateContactForm and
      // InlineEditField.validateField, so all three entry points agree on
      // what counts as a valid email/phone.
      if (trimmedEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) {
        errors.email = 'Please enter a valid email address';
      }
      if (trimmedPhone && !/^[+]?[(]?[0-9]{3}[)]?[-\s.]?[0-9]{3}[-\s.]?[0-9]{4,6}$/.test(trimmedPhone)) {
        errors.phone = 'Please enter a valid phone number';
      }
    }

    if (!formData.account_id) {
      errors.account_id = 'Please select one account';
    }

    if (!formData.contact_role) {
      errors.contact_role = 'Designation is required';
    }

    if (!formData.contact_status) {
      errors.contact_status = 'Contact status is required';
    }

    if (formData.contact_status && !contactStatusOptions.includes(formData.contact_status)) {
      errors.contact_status = 'Please enter valid value for contact status';
    }

    setFormErrors(errors);
    return errors;
  };

  const scrollToFirstError = (errors) => {
    const firstErrorField = fieldOrder.find((field) => errors[field]);
    if (!firstErrorField) return;

    const el = document.getElementById(firstErrorField);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });

      const focusable = el.querySelector('input, [role="button"], textarea');
      if (focusable && typeof focusable.focus === 'function') {
        setTimeout(() => focusable.focus({ preventScroll: true }), 300);
      }
    }
  };

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      aria-labelledby="contact-form-modal-title"
    >
      <Box sx={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: { xs: '90%', sm: '80%', md: '70%', lg: '60%' },
        maxWidth: 800,
        maxHeight: '90vh',
        bgcolor: 'background.paper',
        boxShadow: 24,
        borderRadius: 2,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}>
        <Box className="pearl-popup-header"
          sx={{
            backgroundColor: colors.popupHeader.background,
            px: 4,
            py: 3,
            position: 'sticky',
            top: 0,
            zIndex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <MDTypography 
            id="contact-form-modal-title" 
            variant="h6" 
            component="h2"
            sx={{ color: '#FFFFFF' }}
          >
              {title}
          </MDTypography>
          <IconButton
            aria-label="Close"
            onClick={onClose}
            sx={{ color: '#FFFFFF', ml: 1 }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        <Box 
          sx={{ 
            overflowY: 'auto',
            p: 4,
            '&::-webkit-scrollbar': {
              width: '0.4em'
            },
            '&::-webkit-scrollbar-track': {
              background: '#f1f1f1',
              borderRadius: '4px'
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: '#888',
              borderRadius: '4px'
            },
            '&::-webkit-scrollbar-thumb:hover': {
              background: '#555'
            }
          }}
        >

          {/* Contact Form / Modal */}

          <form onSubmit={handleSaveContact} noValidate>
            <Grid container spacing={2}>

              {/* Salutation */}

              <Grid item xs={12} sm={6} id="title">
                <FormControl fullWidth variant="outlined" margin="normal" error={!!formErrors.title}>
                  <InputLabel>Salutation</InputLabel>
                  <Select
                    name="title"
                    label="Salutation"
                    value={formData.title}
                    onChange={handleInputChange}
                  >
                    {contactTitleOptions.map((status) => (
                      <MenuItem key={status} value={status}>{status}</MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>
                    {formErrors.title}
                  </FormHelperText>
                </FormControl>
              </Grid>

              {/* First Name */}

              <Grid item xs={12} sm={6} id="first_name">
                <TextField
                  fullWidth
                  name="first_name"
                  label="First Name"
                  variant="outlined"
                  margin="normal"
                  InputLabelProps={{ required: true }}
                  value={formData.first_name}
                  onChange={handleInputChange}
                  error={!!formErrors.first_name}
                  helperText={formErrors.first_name}
                />
              </Grid>

              {/* Middle Name */}

              <Grid item xs={12} sm={6} id="middle_name">
                <TextField
                  fullWidth
                  name="middle_name"
                  label="Middle Name"
                  variant="outlined"
                  margin="normal"
                  value={formData.middle_name}
                  error={!!formErrors.middle_name}
                  helperText={formErrors.middle_name}
                  onChange={handleInputChange}
                />
              </Grid>

              {/* Last Name */}

              <Grid item xs={12} sm={6} id="last_name">
                <TextField
                  fullWidth
                  name="last_name"
                  label="Last Name"
                  variant="outlined"
                  margin="normal"
                  InputLabelProps={{ required: true }}
                  value={formData.last_name}
                  onChange={handleInputChange}
                  error={!!formErrors.last_name}
                  helperText={formErrors.last_name}
                />
              </Grid>

              {/* Email */}

              <Grid item xs={12} sm={6} id="email">
                <TextField
                  fullWidth
                  name="email"
                  label="Email"
                  variant="outlined"
                  margin="normal"
                  value={formData.email}
                  onChange={handleInputChange}
                  error={!!formErrors.email}
                  helperText={formErrors.email}
                />
              </Grid>

              {/* Phone */}

              <Grid item xs={12} sm={6} id="phone">
                <TextField
                  fullWidth
                  name="phone"
                  label="Phone"
                  variant="outlined"
                  margin="normal"
                  value={formData.phone}
                  onChange={handleInputChange}
                  error={!!formErrors.phone}
                  helperText={formErrors.phone}
                />
              </Grid>

              {/* Mobile Number */}

              <Grid item xs={12} sm={6} id="mobile_number">
                <TextField
                  fullWidth
                  name="mobile_number"
                  label="Mobile Number"
                  variant="outlined"
                  margin="normal"
                  value={formData.mobile_number}
                  onChange={handleInputChange}
                />
              </Grid>

              {/* Account */}

              <Grid item xs={12} sm={6} id="account_id">
                <Autocomplete
                  options={accounts}
                  getOptionLabel={(option) => option?.name || ''}
                  value={selectedAccount}
                  onChange={handleAccountSelect}
                  onInputChange={(_, newInputValue) => {
                    debouncedFetchAccounts(newInputValue);
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      fullWidth
                      label="Account"
                      variant="outlined"
                      margin="normal"
                      InputLabelProps={{ ...params.InputLabelProps, required: true }}
                      error={!!formErrors.account_id}
                      helperText={formErrors.account_id}
                      onFocus={() => {
                        if (accounts.length === 0) {
                          fetchAccounts(); // Load all accounts when focused
                        }
                      }}
                      InputProps={{
                        ...params.InputProps,
                        style: { height: '45px' },
                        endAdornment: (
                          <React.Fragment>
                            {isLoadingAccounts && <CircularProgress color="inherit" size={20} />}
                            {params.InputProps.endAdornment}
                          </React.Fragment>
                        ),
                      }}
                    />
                  )}
                  isOptionEqualToValue={(option, value) => option?.id === value?.id}
                  loading={isLoadingAccounts}
                />
              </Grid>

              {/* Primary Contact */}

              <Grid item xs={12} sm={6} id="primary_contact">
                <FormControl fullWidth variant="outlined" margin="normal">
                  <InputLabel>Primary Contact</InputLabel>
                  <Select
                    name="primary_contact"
                    value={formData.primary_contact ? "Yes" : "No"}
                    onChange={handleInputChange}
                    label="Primary Contact"

                  >
                    <MenuItem value="Yes">Yes</MenuItem>
                    <MenuItem value="No">No</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {/* Mailing Address */}

              <Grid item xs={12} sm={6} id="mailing_address">
                <MDBox
                    mt={2}
                    mb={1}
                    sx={{
                      '& .MuiOutlinedInput-root, & .MuiInputBase-root': {
                        minHeight: 59,
                        alignItems: 'center',
                      },
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '12px',
                      },
                    }}
                >
                  <AddressAutocompleteInput
                      label="Mailing Address"
                      placeholder="Enter mailing address"
                      value={formData.mailing_address || ''}
                      onChange={(nextAddress) => {
                        setFormData((prev) => ({ ...prev, mailing_address: nextAddress }));
                        setFormErrors((prev) => ({ ...prev, mailing_address: '' }));
                      }}
                      error={Boolean(formErrors.mailing_address)}
                      errorMessage={formErrors.mailing_address}
                  />
                </MDBox>
              </Grid>

              {/* Country */}

              <Grid item xs={12} sm={6} id="country">
                <MDBox mt={2} mb={1}>
                  <CountrySelect
                    label="Country"
                    value={formData.mailing_country || ''}
                    size="small"
                    onChange={(nextCountry) => {
                      setFormData((prev) => ({
                        ...prev,
                        mailing_country: nextCountry || '',
                        mailing_state: '',
                        mailing_city: '',
                      }));
                    }}
                  />
                </MDBox>
              </Grid>

              {/* State */}

              <Grid item xs={12} sm={6} id="mailing_state">
                <MDBox mt={2} mb={1}>
                  <StateSelect
                    label="State"
                    country={formData.mailing_country || ''}
                    value={formData.mailing_state || ''}
                    size="small"
                    onChange={(nextState) => {
                      setFormData((prev) => ({
                        ...prev,
                        mailing_state: nextState || '',
                        mailing_city: '',
                      }));
                    }}
                  />
                </MDBox>
              </Grid>

              {/* City */}

              <Grid item xs={12} sm={6} id="mailing_city">
                <MDBox mt={2} mb={1}>
                  <CitySelect
                    label="City"
                    country={formData.mailing_country || ''}
                    state={formData.mailing_state || ''}
                    value={formData.mailing_city || ''}
                    size="small"
                    onChange={(nextCity) => {
                      setFormData((prev) => ({
                        ...prev,
                        mailing_city: nextCity || '',
                      }));
                    }}
                  />
                </MDBox>
              </Grid>

              {/* Postal Code */}

              <Grid item xs={12} sm={6} id="mailing_postal_code">
                <TextField
                  fullWidth
                  name="mailing_postal_code"
                  label="Postal Code"
                  variant="outlined"
                  margin="normal"
                  value={formData.mailing_postal_code}
                  onChange={handleInputChange}
                />
              </Grid>

              {/* Department */}

              <Grid item xs={12} sm={6} id="department">
                <TextField
                  fullWidth
                  name="department"
                  label="Department"
                  variant="outlined"
                  margin="normal"
                  value={formData.department}
                  onChange={handleInputChange}
                />
              </Grid>

              {/* Designation */}

              <Grid item xs={12} sm={6} id="contact_role">
                <TextField
                  fullWidth
                  name="contact_role"
                  label="Designation"
                  variant="outlined"
                  margin="normal"
                  InputLabelProps={{ required: true }}
                  value={formData.contact_role}
                  onChange={handleInputChange}
                  error={!!formErrors.contact_role}
                  helperText={formErrors.contact_role}
                />
              </Grid>

              {/* Contact Status */}

              <Grid item xs={12} sm={6} id="contact_status">
                <FormControl fullWidth variant="outlined" margin="normal">
                  <InputLabel required>Contact Status</InputLabel>
                  <Select
                    name="contact_status"
                    label="Contact Status"
                    value={formData.contact_status}
                    onChange={handleInputChange}
                    error={!!formErrors.contact_status}

                  >
                    {contactStatusOptions.map((status) => (
                      <MenuItem key={status} value={status}>{status}</MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>{formErrors.contact_status}</FormHelperText>
                </FormControl>
              </Grid>

              {/* <Grid item xs={12} sm={6}>
                <Autocomplete
                  options={managers || []}
                  getOptionLabel={(option) => option.username}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      fullWidth
                      label="Manager"
                      variant="outlined"
                      margin="normal"
                    />
                  )}
                  value={selectedManager}
                  onChange={(_, newValue) => {
                    setSelectedManager(newValue || (user ? { id: user.id, username: user.username } : null));
                  }}
                  isOptionEqualToValue={(option, value) => option?.id === value?.id}
                />
              </Grid> */}

              {/* Notes */}

              <Grid item xs={12} id="notes">
                <TextField
                  fullWidth
                  name="notes"
                  label="Notes"
                  variant="outlined"
                  margin="normal"
                  multiline
                  rows={3}
                  value={formData.notes}
                  onChange={handleInputChange}
                />
              </Grid>

              {/* Add Custom Fields Section */}
              {customFields && customFields.length > 0 && (
                <Grid item xs={12}>
                  <MDBox mt={2}>
                    <MDTypography variant="h6" gutterBottom>
                      Custom Fields
                    </MDTypography>
                    <CustomFieldsSection
                      ref={customFieldsRef}
                      objectType="contacts"
                      objectData={formData}
                      isEditing={true}
                      onFieldUpdate={handleFieldUpdate}
                      onError={handleFieldError}
                      formErrors={formErrors}
                    />
                  </MDBox>
                </Grid>
              )}
            </Grid>

            <MDBox mt={4} mb={1} display="flex" justifyContent="flex-end">
              <MDButton onClick={onClose} color="secondary" sx={{ marginRight: 2 }}>
                Close
              </MDButton>
              <Tooltip title={isSupportUser ? "Support users cannot create contacts" : ""}>
                <span>
                  <MDButton 
                    type="submit" 
                    variant="gradient" 
                    color="info"
                    disabled={isSupportUser || isSaving}
                  >
                    {isSaving ? (
                      <CircularProgress size={24} color="inherit" />
                    ) : (
                      'Save Contact'
                    )}
                  </MDButton>
                </span>
              </Tooltip>
            </MDBox>
          </form>
        </Box>
      </Box>
    </Modal>
  );
};

export default ContactFormModal;